import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileSignature, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { PURCHASING_ROLES } from "@/constants/roles";
import SearchableSelect from "@/components/SearchableSelect";
import ListSearchBar, { LoadMore } from "@/components/ListSearchBar";
import usePagedSearch, { useDebounced } from "@/hooks/usePagedSearch";
import { fmtDate } from "@/lib/utils";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

// Meeting 3 (Package 44) — one list for the whole PO flow: a purchaser
// creates the PO, it's approved (when the rules require it), then they send
// it to the vendor. RFQs are a type of PO. Purchase Requests are no longer
// a separate page — they're just the PO's draft/approval stage.
const STAGE_TABS = [
  { key: "", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "pending_approval", label: "Pending approval" },
  { key: "ready", label: "Ready to send" },
  { key: "with_vendor", label: "With vendor" },
  { key: "closed", label: "Delivered / closed" },
  { key: "rfq", label: "RFQs" },
];

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canBuy = PURCHASING_ROLES.includes(user?.role);
  const [tab, setTab] = useState(null);
  const [search, setSearch] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [jobId, setJobId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Every column sorts on the server, across all pages (Test Drive 2026-10-21).
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [vendors, setVendors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const q = useDebounced(search);
  const current = tab ?? "";

  useEffect(() => {
    api.get("/contractor/vendor-network").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/contractor/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  // Search, filters and paging run on the server across every PO, PO draft and RFQ.
  const list = usePagedSearch("/contractor/purchase-orders/search", {
    q, vendor_id: vendorId, job_id: jobId, date_from: dateFrom, date_to: dateTo,
    sort: sort.key, dir: sort.dir,
    stage: current && current !== "mine" ? current : "", mine: current === "mine",
  });
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "date" || key === "amount" ? "desc" : "asc" }));
  const SortTh = ({ k, label, right }) => (
    <th className={`py-2.5 ${right ? "text-right" : "text-left"}`}>
      <button type="button" onClick={() => toggleSort(k)} data-testid={`po-sort-${k}`} className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-600 ${sort.key === k ? "text-slate-700" : ""}`}>
        {label}
        {sort.key === k ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="opacity-40" />}
      </button>
    </th>
  );
  const counts = list.counts || {};
  useEffect(() => {
    if (tab === null && list.counts) setTab(list.counts.mine > 0 ? "mine" : "");
  }, [list.counts, tab]);

  const urlFor = (r) => (r.kind === "pr" ? `/contractor/purchase-requests/${r.id}` : r.kind === "rfq" ? `/contractor/rfqs/${r.id}` : `/contractor/purchase-orders/${r.id}`);
  const rows = list.items;
  const filtered = Boolean(q || vendorId || jobId || dateFrom || dateTo);

  return (
    <div className="p-6 md:p-8" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Purchase Orders</h1>
          <p className="text-slate-500 text-sm mt-1">Create a PO, get it approved if needed, then send it to the vendor.</p>
        </div>
        {canBuy && (
          <div className="flex gap-2 w-full sm:w-auto">
            <button onClick={() => navigate("/contractor/rfqs/new")} data-testid="new-rfq-button" className="flex-1 sm:flex-none flex items-center justify-center gap-2 h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
              <FileSignature size={14} /> New RFQ
            </button>
            <button onClick={() => navigate("/contractor/purchase-requests/new")} data-testid="new-po-button" className="flex-1 sm:flex-none flex items-center justify-center gap-2 h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
              <Plus size={14} /> New Purchase Order
            </button>
          </div>
        )}
      </div>

      <ListSearchBar value={search} onChange={setSearch} placeholder="Search PO #, RFQ #, title, vendor, requester…" testId="po-search">
        <SearchableSelect value={vendorId} onChange={(e) => setVendorId(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-[13px] min-w-[160px]" data-testid="po-filter-vendor">
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
        </SearchableSelect>
        <SearchableSelect value={jobId} onChange={(e) => setJobId(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-[13px] min-w-[160px]" data-testid="po-filter-job">
          <option value="">All jobs</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
        </SearchableSelect>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="Created from" data-testid="po-filter-date-from" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="Created to" data-testid="po-filter-date-to" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
        {filtered && <button onClick={() => { setSearch(""); setVendorId(""); setJobId(""); setDateFrom(""); setDateTo(""); }} className="h-10 px-3 text-[12.5px] font-semibold text-sky-600">Clear filters</button>}
      </ListSearchBar>

      <div className="flex gap-2 mt-4 flex-wrap">
        {counts.mine > 0 && (
          <button onClick={() => setTab("mine")} data-testid="po-tab-mine" className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium ${current === "mine" ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-800"}`}>
            Waiting on my approval ({counts.mine})
          </button>
        )}
        {STAGE_TABS.map((t) => {
          const n = t.key ? counts[t.key] : counts.all;
          return (
            <button key={t.key || "all"} onClick={() => setTab(t.key)} data-testid={`po-tab-${t.key || "all"}`} className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium ${current === t.key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
              {t.label}{n ? ` (${n.toLocaleString()})` : ""}
            </button>
          );
        })}
      </div>

      {rows === null ? <div className="py-10 text-center text-slate-400 text-sm">Loading…</div> : (
        <>
          <div className="sm:hidden mt-5 space-y-2" data-testid="po-card-list">
            {rows.map((r) => (
              <div key={`${r.kind}-${r.id}`} onClick={() => navigate(urlFor(r))} data-testid={`po-card-${r.number}`} className="border border-slate-200 rounded-xl p-3.5 active:bg-slate-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="mono text-sky-600 font-semibold text-[13px]">{r.number}</span>
                  <StatusBadge status={r.status} />
                </div>
                <div className="mt-1.5 text-[14px] font-medium text-slate-800 truncate">{r.vendor || r.title || "—"}</div>
                <div className="mt-0.5 text-[12px] text-slate-500 truncate">{[r.job, r.purchaser].filter(Boolean).join(" · ")}</div>
                <div className="mt-2 flex items-center justify-between text-[12.5px]">
                  <span className="text-slate-500">{r.kind === "rfq" ? "RFQ" : r.note || (r.date ? fmtDate(r.date) : "")}</span>
                  {r.amount != null && <span className="mono font-bold shrink-0">{fmt(r.amount)}</span>}
                </div>
              </div>
            ))}
            {rows.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">{filtered ? "Nothing matches." : "Nothing here."}</div>}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full mt-5 text-[13px]" data-testid="po-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                  <SortTh k="number" label="PO #" /><SortTh k="purchaser" label="Purchaser" /><SortTh k="date" label="Date" />
                  <SortTh k="vendor" label="Vendor" /><SortTh k="job" label="Job" /><SortTh k="amount" label="Amount" right />
                  <th className="text-left py-2.5 pl-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.kind}-${r.id}`} onClick={() => navigate(urlFor(r))} className="cursor-pointer hover:bg-slate-50 border-b border-slate-50" data-testid={`po-row-${r.number}`}>
                    <td className="py-3 mono text-sky-600 font-medium">{r.number}{r.kind === "rfq" && <span className="ml-2 ss-pill ss-pill-purple">RFQ</span>}</td>
                    <td className="py-3 text-slate-600">{r.purchaser || "—"}</td>
                    <td className="py-3 text-slate-500">{r.date ? fmtDate(r.date) : "—"}</td>
                    <td className="py-3">{r.vendor || <span className="text-slate-400">{r.title || "—"}</span>}</td>
                    <td className="py-3 text-slate-600">{r.job || "—"}</td>
                    <td className="py-3 text-right mono font-medium">{r.amount != null ? fmt(r.amount) : ""}</td>
                    <td className="py-3 pl-3">
                      <StatusBadge status={r.status} />
                      {r.note && <span className="ml-2 text-[11.5px] text-amber-700">{r.note}</span>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400 text-sm">{filtered ? "Nothing matches." : "Nothing here."}</td></tr>}
              </tbody>
            </table>
          </div>
          {current !== "mine" && (
            <LoadMore items={rows} nextCursor={list.nextCursor} loadMore={list.loadMore} loadingMore={list.loadingMore} total={current ? counts[current] : counts.all} />
          )}
        </>
      )}
    </div>
  );
}
